<!-- eslint-disable vue/one-component-per-file -->
<script lang="ts" setup>
import { object, setLocale, string } from 'yup'
import type { ListItem } from '~/types/custom'
import { Iconify } from '~/types/assets'
import { useRoot } from '~/store'

interface ContactsForm {
  name: string | null
  surname: string | null
  email: string | null
  role: ListItem | null
  request: string | null
}

const { openToast } = useRoot()
const { t } = useI18n()

useLocalizedValidations()
useValidationHooks()
const roles = useFormRoles()

const schema = object({
  name: string().required().min(2),
  surname: string().required().min(2),
  email: string().required().email(),
  role: object().required(),
  request: string().required().min(30)
}).required()

const initialValues: ContactsForm = {
  name: null,
  surname: null,
  email: null,
  role: roles[0],
  request: null
}

async function onSubmit(submission: ContactsForm, { resetForm }: { resetForm: () => void }) {
  try {
    const res = await $fetch('/api/mail', {
      method: 'POST',
      body: {
        to: 'lorenzo@rottigni.tech',
        subject: `CONTACT - ${submission.email}`,
        text: `${submission.name} ${submission.surname} \n\n ${submission.role} \n\n ${submission.request}`,
        html: `<h1>${submission.name} ${submission.surname}</h1><div>${submission.role}</div><p>${submission.request}</p>`
      }
    })
    const toast = useToast(t('contacts.alpha.toast'), Iconify.success)
    openToast(toast)

    resetForm()
  } catch (err) {
    console.error(err)
  }
}
</script>

<template lang="pug">
section.contacts__alpha
  KinesisContainer.contacts__alpha__form
    KinesisElement(
      tag="h1"
      :strength="30"
    ) {{  $t('contacts.alpha.title') }}
    KinesisElement.intro(
      tag="p"
      :strength="20"
    ) {{ $t('contacts.alpha.text-content') }}
    KinesisElement(
      :strength="10"
    )
      VForm.v-form(
        v-slot="{ values }"
        :initial-values="initialValues",
        :validation-schema="schema",
        @on-invalid-submit="useInvalidSubmit",
        @on-submit="onSubmit",
      )
        .v-form-row
          .v-form-item
            label.v-form-item__label(
              for="form-name-input"
            ) {{ $t('contacts.alpha.form.name.label') }}
            VField.v-form-item__field(
              id="form-name-input"
              name="name"
              type="text"
              :placeholder="$t('contacts.alpha.form.name.placeholder')"
            )
            VErrorMessage.v-form-item__error(
              name="name"
              as="p"
            )
          .v-form-item
            label.v-form-item__label(
              for="form-surname-input"
            ) {{ $t('contacts.alpha.form.surname.label') }}
            VField.v-form-item__field(
              id="form-surname-input"
              name="surname"
              type="text"
              :placeholder="$t('contacts.alpha.form.surname.placeholder')"
            )
            VErrorMessage.v-form-item__error(
              name="surname"
              as="p"
            )
        .v-form-row
          .v-form-item
            label.v-form-item__label(
              for="form-email-input"
            ) {{ $t('contacts.alpha.form.email.label') }}
            VField.v-form-item__field(
              id="form-email-input"
              name="email"
              type="email"
              placeholder="Mario.rossi@gmail.com"
            )
            VErrorMessage.v-form-item__error(
              name="email"
              as="p"
            )
          .v-form-item
            label.v-form-item__label(
              for="form-role-input"
            ) {{ $t('contacts.alpha.form.role.label') }}
            UIDropdown(
              id="form-role-input"
              :options="roles"
              :fluid="true"
              :option="values.role || roles?.[0]"
              @update:option="(e) => values.role = e"
            )
        .v-form-item.__fluid
          label.v-form-item__label(
            for="form-request-input"
          ) {{ $t('contacts.alpha.form.request.label') }}
          VField.v-form-item__field(
            id="form-request-input",
            :placeholder="$t('contacts.alpha.form.request.placeholder')"
            name="request",
            as="textarea"
            :rows="3"
          )
          VErrorMessage.v-form-item__error(
            name="request",
            as="p"
          )
        div(class="v-form-row justify-center items-center mt-3 lg:mt-6")
          UIButton(
            class-name="bg-secondary text-dark-900 hover:bg-dark-900 hover:text-secondary border-y border-dark-900 hover:border-primary text-sm lg:text-base"
            :binding="{ type: 'submit' }"
          ) {{ $t('contacts.alpha.form.submit') }}
</template>

<style lang="sass" scoped>
.contacts__alpha
  @apply w-full h-full flex-center
  h1
    @apply w-full text-3xl lg:text-4xl text-center mb-2 lg:mb-4 mt-6 lg:mt-0 text-animated font-bold
  p
    @apply text-white text-center text-sm xl:text-base 2xl:text-lg w-10/12 mx-auto pb-6
  p.intro
    @apply hidden lg:block
  .v-form-item__error
    @apply text-left text-sm text-secondary p-0 m-0
  textarea
    @apply scrollbar-primary
</style>
